/**
 * Утилиты для работы с компонентом TagCloud
 */

/**
 * Проверяет, что данные для облака тегов имеют правильный формат
 * @param {Array|string} data - Данные для облака тегов (массив объектов или JSON строка)
 * @param {string} context - Контекст проверки для логгирования
 * @returns {Array} - Правильно форматированные данные или пустой массив
 */
export const validateTagCloudData = (data, context = 'TagCloud') => {
  try {
    console.log(`${context}: Начало валидации данных:`, typeof data);
    
    // Если данные не заданы, возвращаем пустой массив
    if (!data) {
      console.warn(`${context}: Данные не заданы`);
      return [];
    }
    
    // Если данные - строка, пытаемся распарсить
    let parsedData = data;
    if (typeof data === 'string') {
      try {
        console.log(`${context}: Пытаемся распарсить JSON строку`);
        parsedData = JSON.parse(data);
        console.log(`${context}: JSON успешно распарсен:`, typeof parsedData);
      } catch (error) {
        console.error(`${context}: Ошибка при парсинге JSON строки`, error);
        // Попытка обработать строку, которая может быть некорректным JSON
        // Например, если строка была экранирована дважды
        try {
          // Попытка 1: Убираем слеши и кавычки
          let fixedData = data.replace(/\\/g, '').replace(/^"/, '').replace(/"$/, '');
          try {
            parsedData = JSON.parse(fixedData);
            console.log(`${context}: JSON успешно распарсен после исправления:`, typeof parsedData);
          } catch (secondError) {
            // Попытка 2: Возможно двойное экранирование
            try {
              fixedData = fixedData.replace(/^"/, '').replace(/"$/, '');
              parsedData = JSON.parse(fixedData);
              console.log(`${context}: JSON успешно распарсен после повторной обработки:`, typeof parsedData);
            } catch (thirdError) {
              // Попытка 3: Вручную восстанавливаем объект из строки
              if (data.includes('{') && data.includes('}')) {
                try {
                  // Если похоже на массив объектов
                  if (data.includes('[{') && data.includes('}]')) {
                    const regex = /\{[^{}]*\}/g; 
                    const matches = data.match(regex);
                    
                    if (matches && matches.length > 0) {
                      parsedData = matches.map(match => {
                        try {
                          return JSON.parse(match.replace(/\\"/g, '"'));
                        } catch (e) {
                          return null;
                        }
                      }).filter(item => item !== null);
                      
                      if (parsedData.length > 0) {
                        console.log(`${context}: Данные восстановлены вручную:`, parsedData);
                        return parsedData;
                      }
                    }
                  }
                } catch (fourthError) {
                  console.error(`${context}: Все попытки парсинга неудачны`, fourthError);
                }
              }
              
              console.error(`${context}: Повторная ошибка при парсинге JSON строки`, thirdError);
              return [];
            }
          }
        } catch (finalError) {
          console.error(`${context}: Повторная ошибка при парсинге JSON строки`, finalError);
          return [];
        }
      }
    }
    
    // Проверяем, что данные - массив
    if (!Array.isArray(parsedData)) {
      console.error(`${context}: Данные не являются массивом`, parsedData);
      return [];
    }
    
    console.log(`${context}: Данные являются массивом длиной ${parsedData.length}`);
    
    // Проверяем формат и преобразуем если необходимо
    let transformedData = [];
    let needsTransformation = false;
    
    // Проверяем наличие нового формата {definition, tags}
    const hasTagsFormat = parsedData.some(item => 
      item && 
      typeof item === 'object' && 
      'tags' in item && 
      'definition' in item
    );
    
    if (hasTagsFormat) {
      console.log(`${context}: Обнаружен формат с тегами, преобразуем`);
      // Преобразуем формат с тегами в стандартный формат
      transformedData = parsedData.map(item => {
        if (item && typeof item === 'object' && 'tags' in item && 'definition' in item) {
          // Находим правильный тег (с isCorrect: true)
          const correctTag = item.tags.find(tag => tag.isCorrect === true);
          return {
            term: correctTag ? correctTag.text : "Термин не найден",
            definition: item.definition
          };
        }
        // Если элемент уже в правильном формате или неверный, оставляем как есть
        return item;
      });
      needsTransformation = true;
      console.log(`${context}: Данные преобразованы в стандартный формат`);
    }
    
    // Проверяем, что каждый элемент имеет term и definition
    const validData = (needsTransformation ? transformedData : parsedData).filter(item => {
      if (!item || typeof item !== 'object') {
        console.warn(`${context}: Элемент не является объектом`, item);
        return false;
      }
      
      if (!('term' in item) || !('definition' in item)) {
        console.warn(`${context}: Элемент не содержит term или definition`, item);
        return false;
      }
      
      return true;
    });
    
    if (validData.length !== parsedData.length) {
      console.warn(`${context}: Некоторые элементы были отфильтрованы из-за неправильного формата. Осталось ${validData.length} из ${parsedData.length}`);
    }
    
    console.log(`${context}: Валидация завершена, возвращаем ${validData.length} элементов`);
    return validData;
  } catch (error) {
    console.error(`${context}: Непредвиденная ошибка при проверке данных`, error);
    return [];
  }
};

/**
 * Проверяет, являются ли данные для облака тегов корректными
 * @param {Array} data - Данные для облака тегов (массив объектов)
 * @returns {boolean} - true если данные корректны, false в противном случае
 */
export const isValidTagCloudData = (data) => {
  try {
    console.log('isValidTagCloudData: Проверка данных:', typeof data, Array.isArray(data) ? `массив длиной ${data.length}` : data);
    
    if (!Array.isArray(data) || data.length === 0) {
      console.warn('isValidTagCloudData: Данные не являются массивом или пустой массив');
      return false;
    }
    
    // Проверяем, что все элементы имеют term и definition
    // или имеют формат {definition, tags}
    const result = data.every(item => {
      if (!item || typeof item !== 'object') {
        console.warn('isValidTagCloudData: Элемент не является объектом', item);
        return false;
      }
      
      // Проверяем стандартный формат
      const hasStandardFormat = 'term' in item && 'definition' in item;
      
      // Проверяем альтернативный формат
      const hasTagsFormat = 'definition' in item && 
                            'tags' in item && 
                            Array.isArray(item.tags) && 
                            item.tags.some(tag => tag.isCorrect === true);
      
      if (!hasStandardFormat && !hasTagsFormat) {
        console.warn('isValidTagCloudData: Элемент не соответствует ни одному формату', item);
      }
      
      return hasStandardFormat || hasTagsFormat;
    });
    
    console.log('isValidTagCloudData: Результат проверки:', result);
    return result;
  } catch (error) {
    console.error('Ошибка при проверке корректности данных Tag Cloud', error);
    return false;
  }
};

/**
 * Создает пример данных для облака тегов
 * @param {number} count - Количество терминов
 * @returns {Array} - Массив объектов для облака тегов
 */
export const createSampleTagCloudData = (count = 3) => {
  console.log(`createSampleTagCloudData: Создание ${count} тестовых элементов`);
  const sampleData = [];
  
  for (let i = 1; i <= count; i++) {
    sampleData.push({
      term: `Термин ${i}`,
      definition: `Определение для термина ${i}`
    });
  }
  
  console.log(`createSampleTagCloudData: Созданы тестовые данные:`, sampleData);
  return sampleData;
};

const tagCloudUtils = {
  validateTagCloudData,
  isValidTagCloudData,
  createSampleTagCloudData
};

export default tagCloudUtils; 